import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaInfoCircle } from "react-icons/fa";
import { BsCalendarDate } from "react-icons/bs";
import Select from "react-select";
import Cookies from "js-cookie";
import "../css/css_pages/invoice.css";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    width: "100%",
    height: "40px",
    border: state.isFocused ? "1px solid #1c3663" : "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    backgroundColor: "#fcfcfe",
    color: "#444",
    transition: "border-color 0.3s ease",
    boxSizing: "border-box",
    ":hover": {
      borderColor: "#1c3663",
    },
  }),
  option: (base, state) => ({
    ...base,
    color: "#444",
    backgroundColor: state.isSelected ? "#e0e0e0" : "#fcfcfe",
    ":hover": {
      backgroundColor: "#e0e0e0",
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "6px",
    marginTop: 0,
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#444",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#444",
  }),
};

const Invoice = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [invoiceCategories, setInvoiceCategories] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customerFinanceDetails, setCustomerFinanceDetails] = useState({
    net_amount: 0,
    due_date_min: null,
  });
  const [formData, setFormData] = useState({
    client_id: "",
    created_date: new Date(new Date().setDate(new Date().getDate() + 7))
      .toISOString()
      .split("T")[0],
    invoice_no: "",
    invoice_type: "",
    invoice_category: "",
    description: "",
    amount: 0, // Initialize as a number
    due_date: new Date(new Date().setDate(new Date().getDate() + 7))
      .toISOString()
      .split("T")[0],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState({
    clients: false,
    invoiceTypes: false,
    invoiceCategories: false,
    financeDetails: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      const token = Cookies.get("authToken");
      if (!token) {
        setError("Authentication token not found");
        return;
      }

      try {
        // Fetch clients
        setIsLoading((prev) => ({ ...prev, clients: true }));
        const clientsResponse = await fetch(
          `${config.API_URL}/management/get-all-clients`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!clientsResponse.ok) {
          throw new Error("Failed to fetch clients");
        }

        const clientsData = await clientsResponse.json();
        const formattedClients = Array.isArray(clientsData)
          ? clientsData.map((client) => ({
              value: client.id,
              label: `${client.customer_name} (${client.customer_id})`,
              id: client.id,
              customer_id: client.customer_id,
              name: client.customer_name,
            }))
          : [];
        setClients(formattedClients);

        // Fetch invoice types
        setIsLoading((prev) => ({ ...prev, invoiceTypes: true }));
        const typesResponse = await fetch(
          `${config.API_URL}/management/get-invoice-types`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!typesResponse.ok) {
          throw new Error("Failed to fetch invoice types");
        }

        const typesData = await typesResponse.json();
        const safeInvoiceTypes = Array.isArray(typesData.invoice_types)
          ? typesData.invoice_types.map((type) => ({
              name: type.invoice_type_name,
              number: type.invoive_type_number,
              id: type.invoice_type_id,
            }))
          : [];
        setInvoiceTypes(safeInvoiceTypes);
        // Fetch invoice categories
        setIsLoading((prev) => ({ ...prev, invoiceCategories: true }));
        const categoriesResponse = await fetch(
          `${config.API_URL}/management/get-invoice-category-list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!categoriesResponse.ok) {
          throw new Error("Failed to fetch invoice categories");
        }

        const categoriesData = await categoriesResponse.json();
        const formattedCategories = Array.isArray(categoriesData)
          ? categoriesData.map((category) => ({
              value: category.category_id,
              label: category.category_name,
              id: category.category_id,
              name: category.category_name,
            }))
          : [];
        setInvoiceCategories(formattedCategories);

        setError(null);
      } catch (error) {
        console.error("Data fetching error:", error);
        setError(error.message);
        setInvoiceTypes([]);
        setInvoiceCategories([]);
      } finally {
        setIsLoading({
          clients: false,
          invoiceTypes: false,
          invoiceCategories: false,
          financeDetails: false,
        });
      }
    };

    fetchData();
  }, []);

  const fetchCustomerFinanceDetails = async (customerId) => {
    if (!customerId) {
      setCustomerFinanceDetails({ net_amount: 0, due_date_min: null });
      return;
    }

    const token = Cookies.get("authToken");
    if (!token) {
      setError("Authentication token not found");
      return;
    }

    try {
      setIsLoading((prev) => ({ ...prev, financeDetails: true }));
      const response = await fetch(
        `${config.API_URL}/management/get-finance-details/${customerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customer finance details");
      }

      const data = await response.json();
      setCustomerFinanceDetails({
        net_amount: data.net_amount || 0,
        due_date_min: data.due_date_min || null,
      });
    } catch (error) {
      console.error("Error fetching customer finance details:", error);
      setCustomerFinanceDetails({ net_amount: 0, due_date_min: null });
    } finally {
      setIsLoading((prev) => ({ ...prev, financeDetails: false }));
    }
  };

  const handleClientChange = async (selectedOption) => {
    setSelectedClient(selectedOption);
    const clientId = selectedOption ? selectedOption.value : "";

    setFormData({
      ...formData,
      client_id: clientId,
    });

    await fetchCustomerFinanceDetails(clientId);

    if (errors.client_id) {
      setErrors({
        ...errors,
        client_id: null,
      });
    }
  };

  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    const categoryId = selectedOption ? selectedOption.value : "";

    setFormData({
      ...formData,
      invoice_category: categoryId,
    });

    if (errors.invoice_category) {
      setErrors({
        ...errors,
        invoice_category: null,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
      // Convert the value to a number
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0, // Fallback to 0 if parseFloat returns NaN
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.client_id) newErrors.client_id = "Client is required";
    if (!formData.invoice_category)
      newErrors.invoice_category = "Invoice category is required";
    if (!formData.invoice_type)
      newErrors.invoice_type = "Invoice type is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (
      !formData.amount ||
      isNaN(formData.amount) ||
      parseFloat(formData.amount) <= 0
    )
      newErrors.amount = "Valid amount is required";
    if (!formData.due_date) newErrors.due_date = "Due date is required";
    if (!formData.invoice_no.trim())
      newErrors.invoice_no = "Invoice number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showPopupMessage = (message, isError = false) => {
    setAlertMessage({ text: message, isError });
  };

  const handleClosePopup = () => {
    setAlertMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = Cookies.get("authToken");
      const userId = Cookies.get("userId");
      if (!token || !userId) {
        throw new Error("Authentication failed");
      }

      // Find the selected category
      const selectedCategory = invoiceCategories.find(
        (cat) => cat.value === formData.invoice_category,
      );
      if (!selectedCategory) {
        throw new Error("Invalid invoice category selected");
      }

      // Find the selected invoice type
      const selectedType = invoiceTypes.find(
        (type) => type.number.toString() === formData.invoice_type,
      );
      if (!selectedType) {
        throw new Error("Invalid invoice type selected");
      }

      const payload = {
        invoice_number: formData.invoice_no.trim(),
        invoice_type: parseInt(formData.invoice_type),
        invoice_category: selectedCategory.name, // Use the category name instead of the ID
        amount: parseFloat(formData.amount),
        due_date: `${formData.due_date} 08:00:00`, // Ensure proper formatting
        client_id: formData.client_id,
        description: formData.description.trim(),
        created_date: `${formData.created_date} 00:00:00`, // Ensure proper formatting
        net_amount: parseFloat(formData.amount),
        created_by: userId,
      };

      console.log("Submitting payload:", payload); // Debug log

      const response = await fetch(
        `${config.API_URL}/management/create-invoice`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const responseData = await response.json();

      if (!response.ok) {
        console.error("Server response error:", responseData); // Debug log
        throw new Error(
          responseData.message ||
            "Invoice creation failed. Please check the data and try again.",
        );
      }

      showPopupMessage(responseData.message || "Invoice created successfully!");

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          client_id: "",
          created_date: new Date(new Date().setDate(new Date().getDate() + 7))
            .toISOString()
            .split("T")[0],
          invoice_no: "",
          invoice_type: "",
          invoice_category: "",
          description: "",
          amount: 0, // Reset to 0
          due_date: new Date(new Date().setDate(new Date().getDate() + 7))
            .toISOString()
            .split("T")[0],
        });
        setSelectedClient(null);
        setSelectedCategory(null);
        setCustomerFinanceDetails({ net_amount: 0, due_date_min: null });
      }, 2000);
    } catch (error) {
      console.error("Invoice submission error:", error);
      showPopupMessage(
        error.message || "Failed to create invoice. Please try again.",
        true,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="invoice-container">
      <div className="invoice-header">
        <h2>Create New Invoice</h2>
      </div>

      {alertMessage && (
        <PopupMessage
          message={alertMessage.text}
          onClose={handleClosePopup}
          isError={alertMessage.isError}
        />
      )}

      <form onSubmit={handleSubmit} className="invoice-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="client_id">
              Client <span className="required">*</span>
            </label>
            <Select
              value={selectedClient}
              onChange={handleClientChange}
              options={clients}
              isClearable={true}
              placeholder={
                isLoading.clients ? "Loading clients..." : "Select a client"
              }
              isLoading={isLoading.clients}
              loadingMessage={() => "Loading clients..."}
              noOptionsMessage={() =>
                clients.length === 0
                  ? "No clients available"
                  : "No matching clients found"
              }
              styles={customSelectStyles}
              isDisabled={isLoading.clients || isLoading.invoiceTypes}
            />
            {errors.client_id && (
              <span className="error-message">{errors.client_id}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="created_date">
              <FaCalendarAlt /> Invoice Date
            </label>
            <input
              type="date"
              id="created_date"
              name="created_date"
              value={formData.created_date}
              onChange={handleChange}
              className="read-only"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="invoice_category">
              Invoice Category <span className="required">*</span>
            </label>
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={invoiceCategories}
              isClearable={true}
              placeholder={
                isLoading.invoiceCategories
                  ? "Loading categories..."
                  : "Select a category"
              }
              isLoading={isLoading.invoiceCategories}
              loadingMessage={() => "Loading categories..."}
              noOptionsMessage={() =>
                invoiceCategories.length === 0
                  ? "No categories available"
                  : "No matching categories found"
              }
              styles={customSelectStyles}
              isDisabled={isLoading.invoiceCategories}
            />
            {errors.invoice_category && (
              <span className="error-message">{errors.invoice_category}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="invoice_no">
              Invoice Number <span className="required">*</span>
            </label>
            <input
              type="text"
              id="invoice_no"
              name="invoice_no"
              value={formData.invoice_no}
              onChange={handleChange}
              className={errors.invoice_no ? "error" : ""}
              placeholder="Enter invoice number"
            />
            {errors.invoice_no && (
              <span className="error-message">{errors.invoice_no}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="invoice_type">
              Invoice Type <span className="required">*</span>
            </label>
            <select
              id="invoice_type"
              name="invoice_type"
              value={formData.invoice_type}
              onChange={handleChange}
              className={errors.invoice_type ? "error" : ""}
              disabled={isLoading.invoiceTypes}
            >
              <option value="">
                {isLoading.invoiceTypes
                  ? "Loading types..."
                  : "Select invoice type"}
              </option>
              {invoiceTypes.map((type) => (
                <option key={type.id} value={type.number}>
                  {type.name}
                </option>
              ))}
            </select>
            {errors.invoice_type && (
              <span className="error-message">{errors.invoice_type}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            <FaInfoCircle /> Description <span className="required">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className={errors.description ? "error" : ""}
            placeholder="Enter detailed description of services/products"
          />
          {errors.description && (
            <span className="error-message">{errors.description}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="amount">
              Amount (LKR)<span className="required">*</span>
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={errors.amount ? "error" : ""}
              placeholder="0.00"
            />
            {errors.amount && (
              <span className="error-message">{errors.amount}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="current_balance">Current Balance (LKR)</label>
            <input
              type="number"
              id="current_balance"
              name="current_balance"
              value={customerFinanceDetails.net_amount}
              readOnly
              className="read-only"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="due_date">
            <BsCalendarDate /> Due Date <span className="required">*</span>
          </label>
          <input
            type="date"
            id="due_date"
            name="due_date"
            value={formData.due_date}
            onChange={handleChange}
            min={formData.created_date}
            className={errors.due_date ? "error" : ""}
          />
          {errors.due_date && (
            <span className="error-message">{errors.due_date}</span>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={
              isSubmitting ||
              isLoading.clients ||
              isLoading.invoiceTypes ||
              isLoading.invoiceCategories
            }
          >
            {isSubmitting ? "Creating..." : "Create Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Invoice;
